import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, Button, CircularProgress, Alert, Grid
} from '@mui/material';
import { MapPin, Globe, FileText, ExternalLink } from 'lucide-react';
import { getRetreatLocations } from '../../../Services/Retreats/RetreatApis';

// ─── Shared primitives ────────────────────────────────────────────────────────

const SectionTitle = ({ icon: Icon, children }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Icon size={15} color="#FFBD65" strokeWidth={2.2} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E293B', letterSpacing: '0.02em' }}>
            {children}
        </Typography>
    </Box>
);

const InfoRow = ({ label, children, noBorder }) => (
    <Box
        sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'flex-start',
            py: 0.9,
            borderBottom: noBorder ? 'none' : '1px solid #F1F5F9',
            gap: { xs: 0.25, sm: 0 },
        }}
    >
        <Typography
            variant="caption"
            sx={{ color: '#94A3B8', fontWeight: 500, minWidth: { sm: 130 }, pt: { sm: '2px' }, flexShrink: 0 }}
        >
            {label}
        </Typography>
        <Box sx={{ flex: 1, minWidth: 0 }}>
            {typeof children === 'string' || typeof children === 'number' ? (
                <Typography variant="body2" sx={{ fontWeight: 500, color: '#334155' }}>{children}</Typography>
            ) : children}
        </Box>
    </Box>
);

const InfoCard = ({ children, sx }) => (
    <Paper
        elevation={0}
        sx={{ border: '1px solid #E8EEF4', borderRadius: '10px', bgcolor: '#FFFFFF', overflow: 'hidden', ...sx }}
    >
        {children}
    </Paper>
);

const HtmlContent = ({ html }) => (
    <Box
        sx={{
            color: '#475569', fontSize: '0.85rem', lineHeight: 1.65,
            '& p': { mb: 1 },
            '& ul, & ol': { pl: '1.2rem', mb: 1, '& li': { mb: 0.5 } },
            '& *': { fontFamily: 'inherit !important', fontSize: 'inherit !important', color: 'inherit !important' },
        }}
        dangerouslySetInnerHTML={{ __html: html }}
    />
);

const cardPx = { xs: 2, sm: 2.5 };

// ─── Main component ───────────────────────────────────────────────────────────

const LocationTab = ({ retreat }) => {
    const [retreatLocations, setRetreatLocations] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            if (!retreat?.id) return;
            try {
                setLoading(true);
                const response = await getRetreatLocations(retreat.id);
                if (response?.success) {
                    setRetreatLocations(response.data);
                } else {
                    setError('Failed to fetch location information.');
                }
            } catch (err) {
                console.error('Error fetching retreat locations:', err);
                setError('An error occurred while fetching location information.');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [retreat?.id]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress size={28} sx={{ color: '#FFBD65' }} />
            </Box>
        );
    }

    if (error) return <Alert severity="error" sx={{ borderRadius: '8px' }}>{error}</Alert>;

    if (!retreatLocations) {
        return (
            <InfoCard sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">No location information available.</Typography>
            </InfoCard>
        );
    }

    const loc = retreatLocations;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

            {/* ── 1. Address Details ────────────────────────────────────────── */}
            <Box>
                <SectionTitle icon={MapPin}>Address Details</SectionTitle>
                <InfoCard>
                    <Box sx={{ px: cardPx, py: 1.5 }}>
                        <Grid container>
                            <Grid size={{ xs: 12, md: 6 }} sx={{ pr: { md: 3 }, borderRight: { md: '1px solid #F1F5F9' } }}>
                                <InfoRow label="Address">{loc.address || '—'}</InfoRow>
                                <InfoRow label="City">{loc.city || '—'}</InfoRow>
                                <InfoRow label="State" noBorder={true}>{loc.state || '—'}</InfoRow>
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }} sx={{ pl: { md: 3 }, borderTop: { xs: '1px solid #F1F5F9', md: 'none' }, pt: { xs: 1, md: 0 } }}>
                                <InfoRow label="Country">{loc.country || '—'}</InfoRow>
                                <InfoRow label="Pincode">{loc.pincode || '—'}</InfoRow>
                                {(loc.latitude && loc.longitude) && (
                                    <InfoRow label="Coordinates" noBorder>
                                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#334155', fontVariantNumeric: 'tabular-nums' }}>
                                            {loc.latitude}, {loc.longitude}
                                        </Typography>
                                    </InfoRow>
                                )}
                            </Grid>
                        </Grid>
                    </Box>
                </InfoCard>
            </Box>

            {/* ── 2. Maps Link ─────────────────────────────────────────────── */}
            {loc.latitude && loc.longitude && (
                <Box>
                    <SectionTitle icon={Globe}>Map</SectionTitle>
                    <InfoCard>
                        <Box
                            sx={{
                                px: cardPx, py: 1.5,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                flexWrap: 'wrap',
                                gap: 1,
                            }}
                        >
                            <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E293B' }}>
                                    View on Google Maps
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#94A3B8' }}>
                                    {loc.latitude}, {loc.longitude}
                                </Typography>
                            </Box>
                            <Button disableFocusRipple
                                component="a"
                                href={`https://maps.google.com/?q=${loc.latitude},${loc.longitude}`}
                                target="_blank" rel="noopener noreferrer" size="small" variant="outlined" startIcon={<ExternalLink size={13} />}
                                sx={{
                                    flexShrink: 0, borderColor: '#FFBD65', color: '#FFBD65',
                                    textTransform: 'none', fontWeight: 600, borderRadius: '6px',
                                    whiteSpace: 'nowrap', px: 2,
                                }}
                            >
                                Open Maps
                            </Button>
                        </Box>
                    </InfoCard>
                </Box>
            )}

            {/* ── 3. Location Description ───────────────────────────────────── */}
            {loc.description && (
                <Box>
                    <SectionTitle icon={MapPin}>About Location</SectionTitle>
                    <InfoCard>
                        <Box sx={{ px: cardPx, py: 1.5 }}>
                            <HtmlContent html={loc.description} />
                        </Box>
                    </InfoCard>
                </Box>
            )}

            {/* ── 4. Attached Document ─────────────────────────────────────── */}
            {loc.retreatListing?.documentFile && (
                <Box>
                    <SectionTitle icon={FileText}>Documents</SectionTitle>
                    <InfoCard>
                        <Box
                            sx={{
                                px: cardPx, py: 1.5, display: 'flex', alignItems: 'center', gap: 2,
                                flexWrap: { xs: 'wrap', sm: 'nowrap' },
                            }}
                        >
                            <Box sx={{ flexShrink: 0, bgcolor: 'rgba(255,189,101,0.12)', p: 1, borderRadius: '6px', display: 'flex', alignItems: 'center' }}>
                                <FileText size={20} color="#FFBD65" />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, color: '#1E293B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                >
                                    {loc.retreatListing.documentName || 'Document'}
                                </Typography>
                            </Box>
                            <Button disableFocusRipple disableElevation disableTouchRipple
                                component="a"
                                href={loc.retreatLocations?.documentFile || loc.retreatListing.documentFile}
                                target="_blank"
                                rel="noopener noreferrer"
                                size="small"
                                variant="outlined"
                                startIcon={<ExternalLink size={13} />}
                                sx={{
                                    flexShrink: 0, borderColor: '#FFBD65', color: '#FFBD65',
                                    textTransform: 'none', fontWeight: 600, borderRadius: '6px',
                                    whiteSpace: 'nowrap', px: 2,
                                }}
                            >
                                View
                            </Button>
                        </Box>
                    </InfoCard>
                </Box>
            )}

        </Box>
    );
};

export default LocationTab;
